import {
  createContext,
  useContext,
  useEffect,
  useState,
  useCallback,
  type ReactNode,
} from 'react'
import type { User, Session } from '@supabase/supabase-js'
import { supabase, isAuthConfigured, setCachedAccessToken } from '@/lib/supabase'
import { isAbortFetchError, isOfflineOrUnreachableFetchError } from '@/lib/api/core'

type AuthState = {
  session: Session | null
  user: User | null
  isLoading: boolean
  isAuthConfigured: boolean
  /** Org shared secrets admin (derived from JWT metadata; refine when org roles are wired). */
  isAdmin: boolean
  signOut: () => Promise<void>
}

const AuthContext = createContext<AuthState | undefined>(undefined)

export function AuthProvider({ children }: { children: ReactNode }) {
  const [session, setSession] = useState<Session | null>(null)
  const [user, setUser] = useState<User | null>(null)
  const [isLoading, setIsLoading] = useState(isAuthConfigured)

  const applySession = useCallback((next: Session | null) => {
    setSession(next)
    setUser(next?.user ?? null)
    setCachedAccessToken(next?.access_token ?? null)
  }, [])

  const signOut = useCallback(async () => {
    if (supabase) {
      await supabase.auth.signOut()
      applySession(null)
    }
  }, [applySession])

  useEffect(() => {
    const client = supabase
    if (!client) {
      setIsLoading(false)
      return
    }

    const getInitialSession = async (isRetry = false): Promise<boolean> => {
      try {
        const {
          data: { session: initialSession },
          error,
        } = await client.auth.getSession()
        if (error && isAbortFetchError(error)) {
          if (!isRetry) return getInitialSession(true)
          // Lock abort is not a sign-out. Leave loading on so ProtectedLayout
          // does not bounce to /login; onAuthStateChange will settle.
          return false
        }
        if (error && isOfflineOrUnreachableFetchError(error)) {
          await client.auth.signOut({ scope: 'local' })
          applySession(null)
          return true
        }
        applySession(initialSession)
        return true
      } catch (err) {
        if (isAbortFetchError(err)) {
          if (!isRetry) return getInitialSession(true)
          return false
        }
        if (isOfflineOrUnreachableFetchError(err)) {
          await client.auth.signOut({ scope: 'local' })
          applySession(null)
        }
        return true
      }
    }

    void getInitialSession().then((settled) => {
      if (settled) setIsLoading(false)
    })

    const {
      data: { subscription },
    } = client.auth.onAuthStateChange((_event, newSession) => {
      applySession(newSession)
      setIsLoading(false)
    })

    const loadingFallback = window.setTimeout(() => setIsLoading(false), 5000)

    return () => {
      window.clearTimeout(loadingFallback)
      subscription.unsubscribe()
    }
  }, [applySession])

  // Popup OAuth: done page posts to opener; bridge to existing Basecamp listeners.
  useEffect(() => {
    const onMessage = (event: MessageEvent) => {
      if (event.origin !== window.location.origin) return
      if (event.data?.type === 'flownotes-basecamp-connected') {
        window.dispatchEvent(new CustomEvent('basecamp-auth-success'))
      }
      if (event.data?.type === 'flownotes-google-drive-connected') {
        window.dispatchEvent(new CustomEvent('google-drive-auth-success'))
      }
      if (event.data?.type === 'flownotes-google-drive-oauth-error') {
        window.dispatchEvent(
          new CustomEvent('google-drive-auth-error', {
            detail: { error: String(event.data?.error || 'access_denied') },
          }),
        )
      }
    }
    window.addEventListener('message', onMessage)
    return () => window.removeEventListener('message', onMessage)
  }, [])

  const value: AuthState = {
    session,
    user,
    isLoading,
    isAuthConfigured,
    isAdmin: Boolean(
      user?.app_metadata &&
        ((user.app_metadata as Record<string, unknown>).is_admin === true ||
          user.app_metadata.role === 'admin'),
    ),
    signOut,
  }

  return <AuthContext.Provider value={value}>{children}</AuthContext.Provider>
}

export function useAuth(): AuthState {
  const context = useContext(AuthContext)
  if (context === undefined) {
    throw new Error('useAuth must be used within an AuthProvider')
  }
  return context
}
